// Lightweight controller-health probe used by the landing page to
// decide whether to enable the observer / participant tiles.
// Designer is offline-capable so it ignores this hook.
//
// Strategy: GET /api/health on mount, on window focus, and every
// 30 s while the page is open. The response must be 2xx, JSON, and
// contain `{ ok: true }` — anything else is treated as unreachable.
// (Without that strict body check, Vite's SPA history fallback in
// dev returns index.html with a 200, which would otherwise trick
// the probe into reporting a controller that isn't there.)
//
// The `enabled` flag lets the landing page skip the probe entirely
// when there's nothing to authenticate against (no ?lab_id=) — no
// point waking the Worker / Durable Object just to discover we
// couldn't talk to it anyway.

import { apiOrigin } from '@usability-suite/protocol-client';
import { useEffect, useState } from 'react';

export type ControllerHealth =
  | { state: 'unknown' }
  | { state: 'ready' }
  | { state: 'unreachable'; reason: string };

const POLL_INTERVAL_MS = 30_000;

export function useControllerHealth({
  enabled = true,
}: { enabled?: boolean } = {}): ControllerHealth {
  const [health, setHealth] = useState<ControllerHealth>({ state: 'unknown' });

  useEffect(() => {
    if (!enabled) {
      setHealth({ state: 'unknown' });
      return;
    }
    let cancelled = false;
    let timer: number | undefined;

    const probe = async () => {
      try {
        const res = await fetch(apiOrigin() + '/api/health', {
          method: 'GET',
          headers: { Accept: 'application/json' },
          cache: 'no-store',
        });
        if (cancelled) return;
        if (!res.ok) {
          setHealth({ state: 'unreachable', reason: `HTTP ${res.status}` });
          return;
        }
        const ctype = res.headers.get('content-type') ?? '';
        if (!/\bapplication\/json\b/i.test(ctype)) {
          // Dev-server SPA fallback returns index.html with 200 —
          // distinguish a real controller by requiring a JSON body.
          setHealth({
            state: 'unreachable',
            reason: `unexpected content-type: ${ctype || '(none)'}`,
          });
          return;
        }
        let body: unknown;
        try {
          body = await res.json();
        } catch {
          setHealth({ state: 'unreachable', reason: 'invalid JSON body' });
          return;
        }
        if (!body || typeof body !== 'object' || (body as { ok?: unknown }).ok !== true) {
          setHealth({ state: 'unreachable', reason: 'health body did not report ok' });
          return;
        }
        setHealth({ state: 'ready' });
      } catch (err) {
        if (cancelled) return;
        setHealth({
          state: 'unreachable',
          reason: err instanceof Error ? err.message : 'network error',
        });
      }
    };

    void probe();
    timer = window.setInterval(() => void probe(), POLL_INTERVAL_MS);
    const onFocus = () => void probe();
    window.addEventListener('focus', onFocus);

    return () => {
      cancelled = true;
      if (timer) window.clearInterval(timer);
      window.removeEventListener('focus', onFocus);
    };
  }, [enabled]);

  return health;
}
